import * as RadixToast from '@radix-ui/react-toast';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { Button } from './Button';
import styles from './Toast.module.css';
import { useToastStore } from './toastStore';

/**
 * Komunikaty przemijające — w tym „Cofnij" dla optimistic UI (spec 4B).
 *
 * Na Radiksie, a nie własne, bo poprawny komunikat przemijający to nie jest
 * pudełko w rogu: musi zostać ogłoszony czytnikowi ekranu we właściwym trybie
 * (`assertive` dla błędu, `polite` dla potwierdzenia), wstrzymać odliczanie przy
 * najechaniu i przy fokusie, dać się zamknąć klawiszem, wrócić fokusem tam,
 * skąd przyszedł, i nie kraść fokusa w chwili pojawienia się. Radix to ma;
 * własna implementacja zwykle ma pierwsze dwie rzeczy i myśli, że skończyła.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <RadixToast.Provider swipeDirection="right">
      {children}

      {toasts.map((toast) => (
        <RadixToast.Root
          key={toast.id}
          duration={toast.durationMs}
          onOpenChange={(open) => {
            if (!open) dismiss(toast.id);
          }}
          className={[
            styles['toast'],
            toast.tone !== 'neutral' ? styles[toast.tone] : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
          // Błąd przerywa czytanie, potwierdzenie czeka na przerwę — mylenie tych
          // dwóch trybów albo gubi ważny komunikat, albo tnie zdanie w połowie.
          type={toast.tone === 'danger' ? 'foreground' : 'background'}
        >
          <RadixToast.Description className={styles['message']}>
            {toast.message}
          </RadixToast.Description>

          {toast.action ? (
            <RadixToast.Action
              asChild
              altText={toast.action.label}
              className={styles['action']}
            >
              <Button variant="ghost" onClick={toast.action.onAction}>
                {toast.action.label}
              </Button>
            </RadixToast.Action>
          ) : null}

          <RadixToast.Close
            className={styles['close']}
            aria-label={t('common.action.close')}
          >
            <X size={15} aria-hidden="true" />
          </RadixToast.Close>
        </RadixToast.Root>
      ))}

      <RadixToast.Viewport className={styles['viewport']} />
    </RadixToast.Provider>
  );
}
